import { useEffect, useState } from "react";
import toast from "react-hot-toast";

import { useSenderEmails } from "features/comunications/hooks/useSenderEmails";
import { useSendEmail } from "features/patients/hooks/useSendEmail";
import { showMutationError } from "utils/showMutationError";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const useSendEmailForm = ({ patient, documentIds, consultationId, onClose, refetchEmails }) => {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [editorKey, setEditorKey] = useState(0);
  const [senderEmail, setSenderEmail] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [isEditingEmail, setIsEditingEmail] = useState(false);

  useEffect(() => {
    if (patient?.email) {
      setRecipientEmail(patient.email);
      setIsEditingEmail(false);
    } else {
      setRecipientEmail("");
      setIsEditingEmail(true);
    }
  }, [patient?.email]);

  const sendEmail = useSendEmail();
  const { data: senderEmails, isLoading: isLoadingSenderEmails } = useSenderEmails();

  useEffect(() => {
    if (senderEmails?.length > 0 && !senderEmail) {
      setSenderEmail(senderEmails[0]);
    }
  }, [senderEmails, senderEmail]);

  const appendToBody = (content) => {
    setBody((prev) => prev + content);
    setEditorKey((prev) => prev + 1);
  };

  const handleTemplateSelect = (template) => {
    setSubject(template.subject || "");
    setBody(template.template || "");
    setEditorKey((prev) => prev + 1);
  };

  const reset = () => {
    setSubject("");
    setBody("");
    setEditorKey((prev) => prev + 1);
    setRecipientEmail(patient?.email || "");
    setIsEditingEmail(!patient?.email);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const isValidEmail = (value) =>
    typeof value === "string" && EMAIL_REGEX.test(value.trim());

  const handleSend = () => {
    const emailToSend = recipientEmail.trim();
    if (!isValidEmail(emailToSend)) {
      toast.error("Please enter a valid email address");
      setIsEditingEmail(true);

      return;
    }
    if (!subject.trim()) {
      toast.error("Please enter an email subject");

      return;
    }
    if (!body.trim()) {
      toast.error("Please enter an email body");

      return;
    }

    sendEmail.mutate(
      {
        emailAddress: emailToSend,
        patientId: patient.id,
        subject,
        body,
        documentIds,
        senderEmailAddress: senderEmail,
        ...(consultationId && { consultationId }),
      },
      {
        onSuccess: () => {
          toast.success("Email sent successfully");
          refetchEmails?.();
          reset();
        },
        onError: (error) => showMutationError(error, "Failed to send email"),
      }
    );
  };

  const senderEmailOptions = isLoadingSenderEmails
    ? [{ label: "Loading sender emails...", value: "" }]
    : senderEmails?.length > 0
    ? senderEmails.map((e) => ({ label: e, value: e }))
    : [{ label: "No sender emails configured", value: "" }];

  return {
    subject,
    setSubject,
    body,
    setBody,
    editorKey,
    senderEmail,
    setSenderEmail,
    senderEmails,
    isLoadingSenderEmails,
    senderEmailOptions,
    appendToBody,
    handleTemplateSelect,
    handleSend,
    handleClose,
    reset,
    isSending: sendEmail.isPending,
    recipientEmail,
    setRecipientEmail,
    isEditingEmail,
    setIsEditingEmail,
    isValidEmail,
  };
};
